import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';

import { MetaReducer, StoreModule } from '@ngrx/store';
import { TranslateCompiler, TranslateLoader, TranslateModule } from '@ngx-translate/core';

import localeNlBe from '@angular/common/locales/nl-BE';
import {
    AUTOLOGIN,
    ConfigService,
    CoreHttpService,
    DigihavenAuthenticationService,
    DigihavenTaskAutocompleteDataService,
    DigihavenTaskFacetDataService,
    FieldDefinitionService,
    paramEncodingInterceptorProvider,
    RecordNavigationService,
    RecordService,
    SharedModule,
    SharedToastModule,
    ToastHelper,
    ToastService
} from '@digihaven/shared';
import {
    CACHE_CONFIG,
    ConfigService as AbstractConfigService,
    CORE_APP_MODULES,
    ShAbstractRecordDownloadService,
    ShAbstractToastService,
    ShCoreAppModule,
    ShDynamicDialogService,
    ShFieldDefinitionService,
    ShRecordService,
    ShTaskAutocompleteDataService,
    ShTaskFacetDataService
} from '@stylehaven/components';

import { BootstrapRoutingModule } from '@bootstrap/bootstrap-routing.module';
import { RootContainerComponent } from '@bootstrap/containers/root-container.component';
import { AuthGuard } from '@bootstrap/guards/auth/auth.guard';
import { BootstrapSandboxService } from '@bootstrap/sandbox/bootstrap.sandbox.service';
import { EnvironmentConfig } from '@common/entities/common/environment-config.entity';
import { localStorageSyncReducer } from '@common/helpers/local-storage-syncer.helper';
import { tokenInterceptorProviders } from '@common/interceptors/refresh-token';
import { AuthorizationService } from '@common/services/authorization/authorization.service';
import { HttpService } from '@common/services/http/http.service';
import { NavigationService } from '@common/services/navigation/navigation.service';
import { rootReducer } from '@reducers/root.reducer';

import { registerLocaleData } from '@angular/common';
import { DownloadService } from '@common/services/download/download.service';
import {
    APP_NAME_TOKEN,
    AppName,
    CustomTranslateLoader,
    LocaleCode,
    LocaleProvider,
    SharedLanguageService
} from '@language';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';

registerLocaleData(localeNlBe);
const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

@NgModule({
    declarations: [RootContainerComponent],
    imports: [
        // Core App imports
        ...CORE_APP_MODULES,
        ShCoreAppModule.forRoot(CoreHttpService),
        BootstrapRoutingModule,
        StoreModule.forRoot(rootReducer, { metaReducers }),

        // DH Shared imports
        SharedModule.forRoot(HttpService, NavigationService),
        SharedToastModule,

        // App specific imports
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: CustomTranslateLoader,
                deps: [APP_NAME_TOKEN]
            },
            compiler: {
                provide: TranslateCompiler,
                useClass: TranslateMessageFormatCompiler
            }
        })
    ],
    providers: [
        // #region Translations and Locales
        {
            provide: APP_NAME_TOKEN,
            useFactory: (): AppName => {
                return 'edepot';
            }
        },

        {
            provide: APP_INITIALIZER,
            useFactory: (translateService: SharedLanguageService, config: ConfigService<EnvironmentConfig>) => () =>
                config.load('assets/environment.json').then(() => {
                    translateService.init('nl_BE' as LocaleCode);
                }),
            deps: [SharedLanguageService, ConfigService, HttpClient],
            multi: true
        },

        LocaleProvider,
        // #endregion
        {
            provide: AUTOLOGIN,
            useValue: 'digitaalarchief'
        },
        AuthGuard,
        AuthorizationService,
        BootstrapSandboxService,
        ConfigService,
        HttpService,
        paramEncodingInterceptorProvider,
        tokenInterceptorProviders,
        ToastService,
        DigihavenAuthenticationService,
        ShDynamicDialogService,
        {
            provide: FieldDefinitionService,
            deps: [HttpService]
        },
        { provide: RecordNavigationService, useClass: NavigationService },
        {
            provide: ShAbstractRecordDownloadService,
            useClass: DownloadService
        },
        {
            provide: ShAbstractToastService,
            useClass: ToastHelper
        },
        {
            provide: ShRecordService,
            useClass: RecordService,
            deps: [HttpService]
        },
        {
            provide: ShFieldDefinitionService,
            useClass: FieldDefinitionService
        },
        {
            provide: ShTaskFacetDataService,
            useClass: DigihavenTaskFacetDataService
        },
        {
            provide: ShTaskAutocompleteDataService,
            useClass: DigihavenTaskAutocompleteDataService
        },
        {
            provide: AbstractConfigService,
            useClass: ConfigService
        },

        {
            provide: CACHE_CONFIG,
            useValue: [{ url: /profiles(\/|\?)/ }]
        }
    ],
    bootstrap: [RootContainerComponent]
})
export class BootstrapModule {}
